import React from 'react';
import { MapPin, Tag } from 'lucide-react';
import { AppSelectDropdown } from '../AppSelectDropdown';
import { AddressAutocompleteInput } from '../AddressAutocompleteInput';
import type { Contact } from '../../types';
import { leadFromCreateApiResponse } from '../../features/leads/utils/leadFromCreateApiResponse';
import {
  LeadCustomerPicker,
  type LeadCustomerPickerHandle,
  type LeadCustomerSelectionSnapshot,
} from '../../features/leads/components/LeadCustomerPicker';
import { SiteContactPicker } from '../../features/leads/components/SiteContactPicker';
import type { LeadCategoryOption } from '../../features/leads/inboxSuggestLeadCache';
import { leadCategorySwatchClassName } from '../../utils/leadCategoryColors';

export type PlantafelCreatedLeadOption = {
  id: string;
  display_id: string;
  name: string;
  lead_title?: string;
  created_at?: string;
  site_address?: string | null;
};

export type PlantafelInlineCreateProjectHandle = {
  /** Validiert und legt das Projekt an. Wirft bei Validierungs-/API-Fehlern. */
  create: () => Promise<PlantafelCreatedLeadOption>;
};

const EMPTY_FORM = {
  category: '',
  lead_title: '',
  notes: '',
};

const EMPTY_CUSTOMER_SNAP: LeadCustomerSelectionSnapshot = {
  kind: 'none',
  contactType: 'Privatkunde',
  contactId: null,
  organizationId: null,
  address: '',
  creatingNewCustomer: false,
  newPersonChosen: false,
};

type PlantafelInlineCreateProjectProps = {
  onCancel: () => void;
};

const PLANTAFEL_LABEL = 'mb-1 block text-xs font-medium text-app-text-muted';
const PLANTAFEL_INPUT =
  'w-full rounded-md border border-app-border bg-app-surface px-3 py-2 text-sm outline-none focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue';

function selectionKey(snap: LeadCustomerSelectionSnapshot): string {
  return `${snap.kind}:${String(snap.contactId ?? '')}:${snap.organizationId ?? ''}:${snap.newPersonChosen}:${snap.creatingNewCustomer}:${snap.contactType}`;
}

/**
 * Inline „Neues Projekt“-Felder in der Plantafel-Seitenleiste.
 * Speichern erfolgt über den Aktivitäts-Button (via ref.create()).
 */
export const PlantafelInlineCreateProject = React.forwardRef<
  PlantafelInlineCreateProjectHandle,
  PlantafelInlineCreateProjectProps
>(function PlantafelInlineCreateProject({ onCancel }, ref) {
  const [categories, setCategories] = React.useState<LeadCategoryOption[]>([]);
  const [hasDifferentSiteContact, setHasDifferentSiteContact] = React.useState(false);
  const [siteContactDraft, setSiteContactDraft] = React.useState<Contact | null>(null);
  const [siteAddressTouched, setSiteAddressTouched] = React.useState(false);
  const [siteAddress, setSiteAddress] = React.useState('');
  const [formData, setFormData] = React.useState(EMPTY_FORM);
  const [customerSnap, setCustomerSnap] = React.useState<LeadCustomerSelectionSnapshot>(EMPTY_CUSTOMER_SNAP);
  const [categoryError, setCategoryError] = React.useState('');
  const [inquiryError, setInquiryError] = React.useState('');
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const customerPickerRef = React.useRef<LeadCustomerPickerHandle>(null);
  const formDataRef = React.useRef(formData);
  const siteAddressRef = React.useRef(siteAddress);
  const hasDifferentSiteContactRef = React.useRef(hasDifferentSiteContact);
  const siteContactDraftRef = React.useRef(siteContactDraft);
  const siteAddressTouchedRef = React.useRef(siteAddressTouched);
  const lastSelectionKeyRef = React.useRef('');
  formDataRef.current = formData;
  siteAddressRef.current = siteAddress;
  hasDifferentSiteContactRef.current = hasDifferentSiteContact;
  siteContactDraftRef.current = siteContactDraft;
  siteAddressTouchedRef.current = siteAddressTouched;

  React.useEffect(() => {
    fetch('/api/settings/lead-categories', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!Array.isArray(data)) return;
        setCategories(
          data
            .map((row: { key?: string; id?: string; label?: string; color?: string }) => ({
              key: String(row.key ?? row.id ?? ''),
              label: String(row.label ?? ''),
              color: row.color != null ? String(row.color) : undefined,
            }))
            .filter((c) => c.key && c.label),
        );
      })
      .catch(() => {});
  }, []);

  const handleCustomerSelectionChange = React.useCallback((snap: LeadCustomerSelectionSnapshot) => {
    setCustomerSnap(snap);
    const key = selectionKey(snap);
    if (key !== lastSelectionKeyRef.current) {
      lastSelectionKeyRef.current = key;
      setHasDifferentSiteContact(false);
      setSiteContactDraft(null);
      setSiteAddressTouched(false);
      setSiteAddress(snap.address || '');
      return;
    }
    if (
      !siteAddressTouchedRef.current &&
      snap.creatingNewCustomer &&
      snap.contactType === 'Privatkunde'
    ) {
      setSiteAddress(snap.address || '');
    }
  }, []);

  React.useImperativeHandle(ref, () => ({
    create: async () => {
      const data = formDataRef.current;
      const site = siteAddressRef.current;
      const differentSiteContact = hasDifferentSiteContactRef.current;
      const siteContact = siteContactDraftRef.current;

      setCategoryError('');
      setInquiryError('');
      setSaveError(null);

      // Beide Pflichtfelder auf einmal markieren, nicht nur das erste fehlende.
      const titleMissing = !data.lead_title.trim();
      const categoryMissing = !data.category;
      if (titleMissing) setInquiryError('Bitte den Projekt-Titel angeben.');
      if (categoryMissing) setCategoryError('Bitte wählen Sie eine Kategorie.');
      if (titleMissing) throw new Error('Bitte den Projekt-Titel angeben.');
      if (categoryMissing) throw new Error('Bitte wählen Sie eine Kategorie.');

      const resolved = await customerPickerRef.current?.resolveContactId({
        addressFallback: site.trim() || null,
      });
      if (!resolved) {
        throw new Error('Bitte wählen Sie einen Kunden.');
      }
      if (resolved.ok === false) {
        throw new Error(resolved.error || 'Bitte wählen Sie einen Kunden.');
      }

      const body: Record<string, unknown> = {
        category: data.category,
        lead_title: data.lead_title || null,
        notes: data.notes,
        site_address: site.trim() || null,
        contact_id: resolved.contactId,
      };
      if (differentSiteContact && siteContact?.id != null) {
        body.site_contact_id = String(siteContact.id);
      }

      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = (await response.json().catch(() => ({}))) as { error?: string };
        const message = err.error || 'Projekt konnte nicht erstellt werden.';
        setSaveError(message);
        throw new Error(message);
      }
      const raw = (await response.json()) as Record<string, unknown>;
      const lead = leadFromCreateApiResponse(raw);
      return {
        id: String(lead.id),
        display_id: String(lead.display_id ?? ''),
        name: String(lead.name ?? ''),
        lead_title: String(lead.lead_title ?? data.lead_title ?? ''),
        created_at: String(lead.created_at ?? new Date().toISOString()),
        site_address: String(lead.site_address ?? site).trim() || null,
      };
    },
  }));

  const showSiteFields = customerSnap.kind !== 'none';
  const newPrivateCustomer = customerSnap.creatingNewCustomer && customerSnap.contactType === 'Privatkunde';

  return (
    <div className="space-y-3 rounded-lg border border-app-border bg-app-surface-muted p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-app-text">Neues Projekt</p>
        <button
          type="button"
          className="text-xs font-medium text-app-text-subtle hover:text-app-text"
          onClick={onCancel}
        >
          Abbrechen
        </button>
      </div>

      {saveError ? <p className="text-xs text-red-600">{saveError}</p> : null}

      <label className="block text-sm">
        <span className={PLANTAFEL_LABEL}>
          Projekt-Titel <span className="text-red-500">*</span>
        </span>
        <div className="relative">
          <Tag className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={formData.lead_title}
            onChange={(e) => {
              setInquiryError('');
              setFormData((prev) => ({ ...prev, lead_title: e.target.value }));
            }}
            className={`${PLANTAFEL_INPUT} pl-9 ${inquiryError ? 'border-red-400' : ''}`}
            placeholder="z.B. Wärmepumpe"
          />
        </div>
        {inquiryError ? <p className="mt-1 text-xs text-red-600">{inquiryError}</p> : null}
      </label>

      <label className="block text-sm">
        <span className={PLANTAFEL_LABEL}>
          Kategorie <span className="text-red-500">*</span>
        </span>
        <AppSelectDropdown
          value={formData.category}
          onChange={(v) => {
            setCategoryError('');
            setFormData((prev) => ({ ...prev, category: v }));
          }}
          options={[
            { value: '', label: 'Kategorie auswählen' },
            ...categories.map((c) => ({
              value: c.key,
              label: c.label,
              icon: (
                <span
                  className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${leadCategorySwatchClassName(c.color)}`}
                  aria-hidden
                />
              ),
            })),
          ]}
          triggerClassName={categoryError ? 'border-red-400' : undefined}
        />
        {categoryError ? <p className="mt-1 text-xs text-red-600">{categoryError}</p> : null}
      </label>

      <LeadCustomerPicker
        ref={customerPickerRef}
        idPrefix="plantafel-create"
        requiredMarks
        labelClassName={PLANTAFEL_LABEL}
        inputClassName={PLANTAFEL_INPUT}
        triggerClassName=""
        onSelectionChange={handleCustomerSelectionChange}
      />

      {showSiteFields ? (
        <>
          <label className="block text-sm">
            <span className={PLANTAFEL_LABEL}>Baustellenadresse</span>
            <p className="mb-1 text-xs font-normal normal-case tracking-normal text-app-text-subtle">
              {newPrivateCustomer
                ? 'Wird automatisch mit der Kundenadresse befüllt — bei Bedarf anpassbar.'
                : 'Adresse des Objekts. Die Kontaktadresse bleibt unverändert.'}
            </p>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-2.5 top-[11px] z-[1] h-3.5 w-3.5 text-slate-400" />
              <AddressAutocompleteInput
                value={siteAddress}
                onChange={(v) => {
                  setSiteAddressTouched(true);
                  setSiteAddress(v);
                }}
                placeholder="Straße, PLZ Ort"
                inputClassName={`${PLANTAFEL_INPUT} pl-8`}
              />
            </div>
          </label>

          <label className="inline-flex items-center gap-2 text-xs text-app-text-muted">
            <input
              type="checkbox"
              className="rounded border-app-border-strong"
              checked={hasDifferentSiteContact}
              onChange={(e) => {
                const checked = e.target.checked;
                setHasDifferentSiteContact(checked);
                if (!checked) setSiteContactDraft(null);
              }}
            />
            Abweichender Baustellenkontakt
          </label>
          {hasDifferentSiteContact ? (
            <div className="space-y-1">
              <span className={PLANTAFEL_LABEL}>Baustellenkontakt</span>
              <p className="text-xs text-app-text-subtle leading-snug">
                Ansprechpartner für den Monteur vor Ort. Rechnung bleibt beim Projektkontakt.
              </p>
              <SiteContactPicker value={siteContactDraft} onSelect={(c) => setSiteContactDraft(c)} />
            </div>
          ) : null}
        </>
      ) : null}

      <label className="block text-sm">
        <span className={PLANTAFEL_LABEL}>Projektbeschreibung</span>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
          rows={3}
          className={`${PLANTAFEL_INPUT} resize-none`}
          placeholder="Optional…"
        />
      </label>
    </div>
  );
});
